/**
 * HI-SI Creative Portal
 * 
 * Interactive portal for monitoring and managing creative pipeline executions.
 * Displays stage progress, rubric evaluations, iteration history, and media previews.
 * 
 * HISIP-013: Enhanced portal with Creative Profile integration and manifest-first media loading.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence, useAnimation } from 'framer-motion';
import { 
  Brain, 
  Eye, 
  Layers,
  Film,
  PenTool,
  Compass,
  Grid,
  Image,
  MessageSquare,
  Cpu,
  GitBranch,
  Activity,
  Settings,
  CheckCircle,
  Package,
  Music,
  Upload,
  Download,
  Clock
} from 'lucide-react';

// Core components
import FeedbackPanel from '@/components/pipeline/FeedbackPanel';
import CascadePlanner from '@/components/pipeline/CascadePlanner';
import CascadeProgressMonitor from '@/components/pipeline/CascadeProgressMonitor';
import IterationHistory from '@/components/pipeline/IterationHistory';
import InteractiveThresholdSlider from '@/components/pipeline/InteractiveThresholdSlider';
import PersonaBadge from '@/components/pipeline/PersonaBadge';
import AtmosphericBackground from '@/components/futuristic/AtmosphericBackground';
import HolographicCard, { CardContent } from '@/components/futuristic/HolographicCard';
import AudioPlayer from '@/components/ui/AudioPlayer';
import { ConnectionStatusIndicator, ConnectionDot } from '@/components/ui/ConnectionStatus';
import { API_BASE_URL } from '@/lib/api-base';
import { useSecureWebSocketV2 } from '@/hooks/useSecureWebSocket-v2';
// HISI-FE-BRAND-01: Removed getCurrentBrand import - using brandless WebSocket rooms
import { useServerAuth } from '@/hooks/useAuth-cookie';

// FIX-004: Iteration components for iteration modal (still needed for iteration preview)
import {
  IterationPreview,
  IterationTimeline
} from '@/components/pipeline/iteration';

// HISIP-013: New portal components
import RubricBadge, { RubricState } from '@/components/pipeline/RubricBadge';
import ProfileSummary, { ProfileConfig } from '@/components/pipeline/ProfileSummary';

// TEMP-H-001: New React Query components and hooks
import IterationsList from '@/components/IterationsList';
import QCPanel from '@/components/QCPanel';
import { useExecutionSettings, useStageThresholds, useIterations, useQcSummary } from '@/hooks';

// TEMP-H-002: Control plane components
import ExecutionControlBar from '@/components/ExecutionControlBar';
import ExecutionStatusStrip from '@/components/ExecutionStatusStrip';
import StageActions from '@/components/StageActions';
import ControlErrorBoundary from '@/components/ControlErrorBoundary';
import { useControlQueries } from '@/hooks/useControlQueries';

// TEMP-H-003: Timeline components
import ExecutionTimeline from '@/components/ExecutionTimeline';

// Types
import { ExecutionStatus, StageType } from '@/types/execution';
import { ExecutionPortalData, StageData, colorSystem } from '@/types/portal';
import { useDashboardServerStore } from '@/stores/dashboard-server';
import { cn } from '@/utils/cn';
import '@/styles/portal.css';
import '@/styles/futuristic.css';

import { getStageColors } from '@/lib/stage-colors';

// Stage Configuration with tokenized styling
const STAGE_CONFIG = {
  script: {
    name: 'Script',
    icon: PenTool,
    persona: 'The Writer'
  },
  vision: {
    name: 'Vision',
    icon: Eye,
    persona: 'The Architect'
  },
  storyboard: {
    name: 'Storyboard',
    icon: Grid,
    persona: 'The Architect'
  },
  shots: {
    name: 'Shots',
    icon: Film,
    persona: 'The Operator'
  },
  continuity: {
    name: 'Continuity',
    icon: Compass,
    persona: 'The Operator'
  },
  frames: {
    name: 'Frames',
    icon: Image,
    persona: 'The Operator'
  },
  videos: {
    name: 'Videos',
    icon: Layers,
    persona: 'The Operator'
  },
  elements: {
    name: 'Elements',
    icon: Layers,
    persona: 'The Compositor'
  },
  qc: {
    name: 'QC',
    icon: CheckCircle,
    persona: 'The Inspector'
  },
  assemble: {
    name: 'Assemble',
    icon: Package,
    persona: 'The Editor'
  },
  music: {
    name: 'Music',
    icon: Music,
    persona: 'The Composer'
  },
  publish: {
    name: 'Publish',
    icon: Upload,
    persona: 'The Publisher'
  },
  music_master: {
    name: 'Music Master',
    icon: Music,
    persona: 'The Sound Engineer'
  },
  music_sidechain: {
    name: 'Music Sidechain',
    icon: Music,
    persona: 'The Sound Engineer'
  },
  export_preview: {
    name: 'Export Preview',
    icon: Download,
    persona: 'The Finalizer'
  }
};

const STAGES: StageType[] = ['script', 'vision', 'storyboard', 'shots', 'continuity', 'frames', 'videos', 'elements', 'qc', 'assemble', 'music', 'publish', 'music_master', 'music_sidechain', 'export_preview'];

interface StageNodeProps {
  stage: StageType;
  execution: ExecutionPortalData;
  isActive: boolean;
  isSelected: boolean;
  onClick: () => void;
  onFeedback: () => void;
  onIterations: () => void;
  qualityPercent?: number;
  iterationsCount?: number;
  showEnhancedIndicators?: boolean;
  rubricByStage: Record<string, RubricState>;
}

const StageNode: React.FC<StageNodeProps> = ({ 
  stage, 
  execution, 
  isActive, 
  isSelected, 
  onClick, 
  onFeedback,
  onIterations,
  qualityPercent,
  iterationsCount,
  showEnhancedIndicators = false,
  rubricByStage
}) => {
  const config = STAGE_CONFIG[stage];
  const stageColors = getStageColors(stage);
  const Icon = config.icon;
  const controls = useAnimation();
  
  // Get stage-specific data
  const stageData = execution.stages?.[stage] || { status: 'pending', iterations: 0, quality: 0 };
  const iterations = typeof iterationsCount === 'number' ? iterationsCount : stageData.iterations;
  const quality = typeof qualityPercent === 'number' ? qualityPercent : stageData.quality;
  const status = stageData.status;
  const isRunning = status === 'running';
  const isComplete = status === 'completed';
  const isFailed = status === 'failed';
  
  // Animate when active - synchronized with brain pulse (3s duration)
  useEffect(() => {
    if (isActive || isRunning) {
      controls.start({
        scale: [1, 1.05, 1],
        transition: { 
          duration: 3, // Note: Framer Motion requires numeric values, but this syncs with --motion-duration-slow (3s)
          repeat: Infinity,
          ease: "easeInOut"
        }
      });
    } else {
      controls.start({ scale: 1 });
    }
  }, [isActive, isRunning, controls]);

  return (
    <motion.div
      animate={controls}
      className="relative group cursor-pointer focus:outline-none"
      onClick={onClick}
      style={{
        animationName: 'fadeIn',
        animationDuration: `calc(var(--motion-duration-normal) + ${STAGES.indexOf(stage) * 0.1}s)`, // Staggered animation using motion tokens
        animationTimingFunction: 'ease-out',
        outline: 'none',
        border: 'none'
      }}
    >
      {/* Translucent outer ring */}
      <div 
        className="absolute -inset-4 rounded-full transition-all"
        style={{
          background: isSelected ? `radial-gradient(circle, color-mix(in srgb, ${stageColors.primaryColor} 30%, transparent) 0%, transparent 70%)` : 
                     isRunning ? `radial-gradient(circle, color-mix(in srgb, var(--accent) 20%, transparent) 0%, transparent 70%)` :
                     'transparent',
          animation: isRunning ? `pulse var(--motion-duration-slower) infinite` : undefined,
          transitionDuration: 'var(--motion-duration-normal)'
        }}
      />
      
      {/* Glow effect for selected/quality states */}
      {(isSelected || isComplete) && (
        <div 
          className="absolute -inset-2 rounded-full blur-xl transition-all"
          style={{
            background: stageColors.glowColor,
            animation: isRunning ? `pulse var(--motion-duration-slower) infinite var(--motion-ease-standard)` : undefined,
            transitionDuration: 'var(--motion-duration-normal)'
          }}
        />
      )}
      
      {/* Main circular container */}
      <div 
        className="relative rounded-full focus:outline-none backdrop-blur-xl transition-all"
        style={{
          width: 'var(--space-16)',
          height: 'var(--space-16)',
          backgroundColor: 'var(--bg-surface)',
          border: `2px solid ${
            isRunning ? 'var(--accent)' :
            isComplete && quality >= 85 ? 'var(--success)' :
            isComplete && quality >= 70 && quality < 85 ? 'var(--warning)' :
            isComplete && quality < 70 ? 'var(--danger)' :
            isFailed ? 'var(--danger)' :
            'var(--border-subtle)'
          }`,
          transform: isSelected ? 'scale(1.1)' : 'scale(1)',
          zIndex: isSelected ? 20 : 'auto',
          transitionDuration: 'var(--motion-duration-fast)',
          animation: isRunning ? `pulse var(--motion-duration-fast) infinite` : undefined,
          boxShadow: isSelected ? `
            0 0 40px ${stageColors.glowColor},
            0 0 80px ${stageColors.glowColor}
          ` : isComplete ? `
            0 0 30px ${stageColors.glowColor}
          ` : isRunning ? `
            0 0 25px ${stageColors.glowColor}
          ` : 'none',
          outline: 'none'
        }}>

        {/* Content */}
        <div className="relative h-full flex flex-col items-center justify-center">
          {/* Icon with glow */}
          <div 
            style={{
              marginBottom: 'var(--space-2)',
              color: isSelected || isRunning || isComplete ? stageColors.primaryColor : 'var(--fg-secondary)',
              filter: isSelected ? `drop-shadow(0 0 20px ${stageColors.glowColor})` : 
                     isRunning ? `drop-shadow(0 0 15px ${stageColors.glowColor})` : 'none',
              animation: isRunning ? `pulse var(--motion-duration-slower) infinite var(--motion-ease-standard)` : undefined
            }}
          >
            <Icon style={{ width: 'var(--space-10)', height: 'var(--space-10)' }} />
          </div>

          {/* Stage name */}
          <div 
            className="font-medium tracking-wide uppercase transition-all"
            style={{
              fontSize: 'var(--font-size-xs)',
              color: isSelected ? 'var(--fg-primary)' : 'var(--fg-secondary)',
              textShadow: isSelected ? `0 0 10px ${stageColors.glowColor}` : 'none',
              transitionDuration: 'var(--motion-duration-fast)'
            }}
          >
            {config.name}
          </div>

          {/* Quality score */}
          {quality > 0 && (
            <div className="flex items-baseline justify-center mt-1">
              <span 
                className="font-mono font-bold"
                style={{
                  fontSize: 'var(--font-size-2xl)',
                  color: quality >= 85 ? 'var(--success)' :
                         quality >= 70 ? 'var(--warning)' :
                         'var(--danger)',
                  textShadow: `0 0 10px currentColor`
                }}
              >
                {Math.round(quality)}
              </span>
              <span 
                className="font-mono"
                style={{
                  fontSize: 'var(--font-size-xs)',
                  marginLeft: 'var(--space-1)',
                  opacity: 0.7,
                  color: 'var(--fg-disabled)'
                }}
              >
                %
              </span>
            </div>
          )}

        </div>
      </div>

      {/* HISIP-013: Rubric Badge */}
      <div className="absolute -top-3 -left-3 z-10">
        <RubricBadge
          rubric={rubricByStage[stage]}
          size="md"
          showScore={false}
          showTooltip={true}
        />
      </div>

      {/* Iteration badge with enhanced indicators */}
      {iterations > 0 && (
        <motion.div 
          className="absolute -top-2 -right-2 z-10"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          whileHover={{ scale: 1.2 }}
        >
          <button
            onClick={(e) => { e.stopPropagation(); onIterations(); }}
            className={cn(
              "w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white shadow-lg cursor-pointer",
              showEnhancedIndicators 
                ? "bg-gradient-to-br from-emerald-500 to-green-500" 
                : "bg-gradient-to-br from-cyan-500 to-blue-500"
            )}
            style={{
              boxShadow: showEnhancedIndicators 
                ? '0 0 20px color-mix(in srgb, var(--success) 60%, transparent)' 
                : '0 0 20px color-mix(in srgb, var(--info) 60%, transparent)'
            }}
            aria-label={showEnhancedIndicators ? "View enhanced iterations (FIX-004)" : "View iterations"}
            title={showEnhancedIndicators ? "Enhanced View (FIX-004)" : "View iterations"}
          >
            {iterations}
            {showEnhancedIndicators && (
              <motion.div
                className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-green-400 rounded-full"
                animate={{ scale: [1, 1.5, 1] }}
                transition={{ duration: 1, repeat: Infinity }} // Note: Framer Motion requires numeric values
              />
            )}
          </button>
        </motion.div>
      )}
      
      {/* Enhanced feedback button with glow */}
      {isComplete && (
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          whileHover={{ scale: 1.2 }}
          whileTap={{ scale: 0.9 }}
          onClick={(e) => {
            e.stopPropagation();
            onFeedback();
          }}
          className="absolute -bottom-3 right-0 p-1.5 rounded-full bg-purple-500 hover:bg-purple-600 transition-all shadow-lg"
          style={{
            boxShadow: '0 0 20px color-mix(in srgb, var(--effect-purple) 50%, transparent)'
          }}
        >
          <MessageSquare className="w-3 h-3" />
        </motion.button>
      )}
    </motion.div>
  );
};

// Helper function to transform ExecutionStatus to ExecutionPortalData
const transformExecutionData = (execution: ExecutionStatus, outputs?: Record<StageType, any>): ExecutionPortalData => {
  const stages: Record<StageType, StageData> = {} as Record<StageType, StageData>;
  
  // Initialize all stages with default data
  STAGES.forEach(stage => {
    const stageIndex = STAGES.indexOf(stage);
    const currentIndex = STAGES.indexOf(execution.current_stage);
    
    stages[stage] = {
      status: stageIndex < currentIndex ? 'completed' : 
              stageIndex === currentIndex ? 'running' : 'pending',
      iterations: 0,
      quality: stageIndex <= currentIndex ? execution.avg_quality_score : 0,
      persona: STAGE_CONFIG[stage].persona,
      // Add audio URL for script stage if available
      audioUrl: stage === 'script' && execution.metadata?.audioUrl ? 
                execution.metadata.audioUrl : undefined,
      output: outputs?.[stage]
    };
  });
  
  return {
    ...execution,
    stages
  };
};

// Performance tracking utilities
const usePerformanceTracker = () => {
  const metrics = useRef<Record<string, number>>({});
  const startTime = useRef<Record<string, number>>({});
  
  const start = useCallback((metric: string) => {
    startTime.current[metric] = performance.now();
  }, []);
  
  const end = useCallback((metric: string) => {
    if (startTime.current[metric]) {
      const duration = performance.now() - startTime.current[metric];
      metrics.current[metric] = duration;
      delete startTime.current[metric];
      
      // Log performance metrics (sampled at 10%)
      if (Math.random() < 0.1) {
        console.log(`[PERF] ${metric}: ${duration.toFixed(2)}ms`);
      }
      
      return duration;
    }
    return 0;
  }, []);
  
  const getMetrics = useCallback(() => ({ ...metrics.current }), []);
  
  return { start, end, getMetrics };
};

export default function ExecutionPortal() {
  const { executionId } = useParams<{ executionId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const executions = useDashboardServerStore(state => state.executions);
  // Server auth is available if needed
  useServerAuth();
  
  // Performance tracking
  const perf = usePerformanceTracker();
  
  // Secure WebSocket with automatic connection management
  const webSocketState = useSecureWebSocketV2({
    executionId: executionId || '',
    enablePollingFallback: true,
    onConnectionChange: (status) => {
      console.log(`🔌 ExecutionPortal WebSocket status: ${status}`);
    },
    onAuthFailure: (error) => {
      console.warn(`🔒 ExecutionPortal WebSocket auth failed: ${error}`);
    }
  });
  
  // State
  const [selectedStage, setSelectedStage] = useState<StageType | null>(null);
  const [showFeedbackPanel, setShowFeedbackPanel] = useState(false);
  const [activeFeedbackStage, setActiveFeedbackStage] = useState<StageType | null>(null);
  const [showCascadePlanner, setShowCascadePlanner] = useState(false);
  const [activeCascadeId, setActiveCascadeId] = useState<string | null>(null);
  const [showIterationHistory, setShowIterationHistory] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [iterationsByStage, setIterationsByStage] = useState<Record<string, any[]>>({});
  const [qualityMetricsByStage, setQualityMetricsByStage] = useState<Record<string, any>>({});
  const [avgQualityPercent, setAvgQualityPercent] = useState<number>(0);
  const [outputsByStage, setOutputsByStage] = useState<Record<StageType, any>>({} as Record<StageType, any>);
  
  // HISIP-013: Enhanced state management
  const [executionData, setExecutionData] = useState<any>(null);
  const [rubricByStage, setRubricByStage] = useState<Record<string, RubricState>>({});
  const [profileConfig] = useState<ProfileConfig | null>(null);
  const [showIterationDetailsModal, setShowIterationDetailsModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound] = useState(false);

  // TEMP-H-001: React Query hooks for data fetching
  useExecutionSettings(executionId || '');
  const { data: stageThresholds } = useStageThresholds(executionId || '', selectedStage || 'script');
  useIterations(executionId || '', selectedStage || 'script');
  useQcSummary(executionId || '', selectedStage || 'script');

  // TEMP-H-002: Control plane hooks
  const { data: controlQueries } = useControlQueries(executionId || '');

  // Get execution data and transform it
  const rawExecution = executionId ? executions.get(executionId) : null;
  
  // Get real-time metadata from store (updated by WebSocket events)
  const storeIterations = rawExecution?.metadata?.iterations_by_stage || {};
  const storeMaxIterations = rawExecution?.metadata?.max_iterations || {};
  const storeOutputs = rawExecution?.metadata?.outputs_by_stage || {};
  const storeRubric = rawExecution?.metadata?.rubric_by_stage || {};

  // TODO: Replace with backend endpoint - no threshold update API available yet
  const updateStageThresholds = useCallback(async () => {
    console.warn('updateStageThresholds: Backend update API not yet implemented - threshold changes will not persist');
    // TEMP: For now, this is a no-op since backend has no update endpoint
    // Backend needs to implement PUT /executions/{id}/stages/{stage}/thresholds
  }, []);
  
  // Normalize stage output content for UI consumption
  const normalizeStageOutput = (content: any) => {
    try {
      if (content == null) return undefined;

      if (typeof content === 'string') {
        return { type: 'text', content };
      }

      if (typeof content === 'object' && (content.script_text || content.text)) {
        return { type: 'text', content: content.script_text || content.text };
      }

      const segments = content.video_segments;
      if (Array.isArray(segments) && segments.length > 0) {
        const seg = segments[0];
        const url = seg?.preview_url || seg?.video_url;
        if (url) return { type: 'video', url };
      }

      if (content.image_url || content.preview_url || content.url) {
        const url = content.image_url || content.preview_url || content.url;
        return { type: 'image', url };
      }
      const frames = content.generated_frames || content.frames;
      if (Array.isArray(frames) && frames.length > 0 && (frames[0].image_url || frames[0].url)) {
        const url = frames[0].image_url || frames[0].url;
        return { type: 'image', url };
      }

      return { type: 'text', content: JSON.stringify(content, null, 2) };
    } catch {
      return { type: 'text', content: String(content) };
    }
  };
  
  // Merge store metadata with local state when store updates
  useEffect(() => {
    if (storeIterations && Object.keys(storeIterations).length > 0) {
      console.log('📊 Updating from store metadata:', { storeIterations, storeMaxIterations });
      setIterationsByStage(prev => ({ ...prev, ...storeIterations }));
    }
    if (storeOutputs && Object.keys(storeOutputs).length > 0) {
      const normalizedOutputs: Record<StageType, any> = {} as Record<StageType, any>;
      Object.entries(storeOutputs).forEach(([stage, output]: [string, any]) => {
        if (output?.content) {
          normalizedOutputs[stage as StageType] = normalizeStageOutput(output.content);
        }
      });
      setOutputsByStage(prev => ({ ...prev, ...normalizedOutputs }));
    }
    if (storeRubric && Object.keys(storeRubric).length > 0) {
      setRubricByStage(prev => ({ ...prev, ...storeRubric }));
    }
  }, [storeIterations, storeOutputs, storeRubric, storeMaxIterations, normalizeStageOutput]);
  
  // Use new execution data or fallback to transformed legacy data
  const execution = executionData ? transformExecutionData(executionData, outputsByStage) : 
                    (rawExecution ? transformExecutionData(rawExecution, outputsByStage) : null);
  
  // HISIP-013: Load execution data from new API endpoint
  useEffect(() => {
    if (!executionId) return;
    let cancelled = false;
    
    const loadExecutionData = async () => {
      perf.start('portal_data_load');
      setLoading(true);
      setError(null);
      
      try {
        // TODO: Remove this function - should use React Query hooks
        console.warn('loadExecutionData: Should be replaced with React Query hooks');
        
        // TEMP: Stub out API call - should use React Query hooks
        console.warn('API call stubbed - should use React Query hooks');
        return;
        
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load execution data:', err);
          setError(err instanceof Error ? err.message : 'Failed to load execution data');
          
          // Fallback to dashboard store data if API fails
          const fallbackExecution = executions.get(executionId);
          if (fallbackExecution) {
            setExecutionData(fallbackExecution);
            setAvgQualityPercent(fallbackExecution.avg_quality_score || 0);
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          perf.end('portal_data_load');
        }
      }
    };
    
    loadExecutionData();
    return () => { cancelled = true; };
  }, [executionId, executions]);

  // Listen for store-level refresh events to invalidate React Query caches
  useEffect(() => {
    const handler = (e: any) => {
      if (!executionId) return;
      const { execution_id, reason, stage } = e.detail || {};
      if (execution_id && execution_id !== executionId) return;
      
      console.log('[ExecutionPortal] Portal refresh event received:', { 
        execution_id, 
        reason, 
        stage,
        executionId 
      });
      
      // Invalidate React Query caches for live updates
      console.log('[ExecutionPortal] Invalidating React Query caches for:', { execution_id, reason, stage });
      
      // Invalidate execution-level queries
      queryClient.invalidateQueries({ queryKey: ['execution', executionId, 'settings'] });
      queryClient.invalidateQueries({ queryKey: ['execution', executionId, 'thresholds'] });
      queryClient.invalidateQueries({ queryKey: ['execution', executionId, 'qc-summary'] });
      queryClient.invalidateQueries({ queryKey: ['execution', executionId, 'status'] });
      
      // Stage-specific invalidations if stage is provided
      if (stage) {
        queryClient.invalidateQueries({ queryKey: ['execution', executionId, 'stage', stage, 'iterations'] });
        queryClient.invalidateQueries({ queryKey: ['execution', executionId, 'stage', stage, 'settings'] });
        queryClient.invalidateQueries({ queryKey: ['execution', executionId, 'stage', stage, 'personas'] });
      }
      
      // Invalidate timeline events for live event updates
      queryClient.invalidateQueries({ queryKey: ['execution', executionId, 'events'] });
    };
    
    window.addEventListener('portal:refresh' as any, handler as any);
    return () => window.removeEventListener('portal:refresh' as any, handler as any);
  }, [executionId]);
  
  // Performance tracking for portal rendering
  useEffect(() => {
    if (!execution?.stages) return;
    
    perf.start('portal_render');
    
    // Track component counts for observability
    const componentCounts = {
      total_stages: STAGES.length,
      active_stages: STAGES.filter(s => execution.stages[s]?.status === 'running').length,
      completed_stages: STAGES.filter(s => execution.stages[s]?.status === 'completed').length,
      failed_stages: STAGES.filter(s => execution.stages[s]?.status === 'failed').length,
      profile_configured: !!(execution as any).profile,
      iterations_loaded: Object.keys((execution as any).iterations || {}).length,
      rubrics_displayed: Object.keys((execution as any).rubric_by_stage || {}).length
    };
    
    // Report metrics to console (heavily sampled for debugging)
    if (Math.random() < 0.001) { // 0.1% sampling - effectively disabled
      console.log('[PORTAL_METRICS]', {
        execution_id: executionId,
        ...componentCounts,
        performance_metrics: perf.getMetrics()
      });
    }
    
    // End render timing on next tick
    requestAnimationFrame(() => {
      perf.end('portal_render');
    });
    
  }, [execution, executionId, perf]);
  
  // Pre-fetch related data for performance
  useEffect(() => {
    if (!execution?.stages) return;
    
    const preFetchRelatedData = async () => {
      // TODO: Replace with authenticated fetch using React Query
      console.warn('preFetchRelatedData: Should use React Query with auth');
      return;
      
      // Pre-fetch manifest data for active/completed stages
      if (!execution) return;
      const activeStages = STAGES.filter(stage => 
        execution!.stages[stage]?.status === 'completed' || 
        execution!.stages[stage]?.status === 'running'
      );
      
      // Pre-fetch manifests in the background (don't await)
      activeStages.forEach(async (stage) => {
        if ((execution as any).curriculum_id && (execution as any).lesson_id) {
          try {
            await fetch(`${API_BASE_URL}/api/storage/manifest?project_id=${(execution as any).curriculum_id}&asset_id=${(execution as any).lesson_id}&stage=${stage}`, {
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
              },
            });
            // Data will be cached by browser for later use
          } catch (error) {
            // Silently fail - this is just pre-fetching
            console.debug('Pre-fetch failed for stage', stage, error);
          }
        }
      });
      
      // Pre-fetch iterations for active stages
      if (activeStages.length > 0) {
        try {
          await fetch(`${API_BASE_URL}/api/executions/${executionId}/iterations`, {
            credentials: 'include'
          });
        } catch (error) {
          console.debug('Pre-fetch iterations failed', error);
        }
      }
    };
    
    // Delay pre-fetching to not block initial render
    const preFetchTimeout = setTimeout(preFetchRelatedData, 500);
    return () => clearTimeout(preFetchTimeout);
  }, [execution, executionId]);

  // Refresh iterations when the Iteration History modal opens, and subscribe to realtime updates
  useEffect(() => {
    if (!showIterationHistory || !selectedStage || !executionId) return;

    let cancelled = false;
    // TODO: Remove Supabase realtime - should use WebSocket or React Query
    let channel: any = null;

    const fetchIterations = async () => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/executions/${executionId}/iterations?stage=${selectedStage}`, {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        if (!resp.ok) return;
        const json = await resp.json();
        if (cancelled) return;
        const list = Array.isArray(json.iterations) ? json.iterations : [];
        setIterationsByStage(prev => ({ ...prev, [selectedStage]: list }));
      } catch (e) {
        console.debug('Failed to refresh iterations', e);
      }
    };

    // Initial load when opened
    fetchIterations();

    // TODO: Replace Supabase realtime with WebSocket or React Query polling
    console.warn('Supabase realtime subscription removed - should use WebSocket');
    // The WebSocket should handle realtime updates via cache invalidation

    // Light polling as a fallback (handles cases where realtime is throttled)
    const poll = setInterval(fetchIterations, 4000);

    return () => {
      cancelled = true;
      clearInterval(poll);
      if (channel) {
        // TODO: Remove Supabase channel cleanup
      }
    };
  }, [showIterationHistory, selectedStage, executionId]);
  
  useEffect(() => {
    if (!executionId || !execution) return;
    
    // Use real execution data - no need to fetch if we already have it
    // The iterations data should come from the execution object or existing state
    // Note: execution doesn't have iterations directly, using existing state
    
    // Set quality metrics from real execution data
    if (execution.stages) {
      const qualityMetrics: Record<string, any> = {};
      const iterations: Record<string, any[]> = {};
      
      STAGES.forEach(stage => {
        const stageData = execution.stages[stage];
        if (stageData) {
          // Set quality metrics if available
          if (stageData.quality) {
            qualityMetrics[stage] = {
              overall: stageData.quality / 100,
              quality: stageData.quality / 100
            };
          }
          
          // Create iterations data for all stages with iterations
          if (stageData.iterations && stageData.iterations > 0) {
            const iterationCount = stageData.iterations;
            const qualityScore = stageData.quality || 0;
            iterations[stage] = Array.from({ length: iterationCount }, (_, i) => ({
              iteration_number: i + 1,
              stage,
              decision: i === iterationCount - 1 ? 'accept' : 'improve',
              scores: {
                overall: qualityScore / 100,
                quality: qualityScore / 100
              },
              created_at: new Date(Date.now() - (iterationCount - i) * 300000).toISOString(),
              is_best_attempt: i === iterationCount - 1,
              content: `Iteration ${i + 1} content for ${stage}`,
              feedback: `Iteration ${i + 1} feedback for ${stage}`
            }));
          }
        }
      });
      
      setQualityMetricsByStage(qualityMetrics);
      setIterationsByStage(iterations);
      setAvgQualityPercent(execution.avg_quality_score || 0);
    }
  }, [executionId, execution]);
  
  // TODO: Session management now handled by React Query auth state
  useEffect(() => {
    // Empty useEffect - session management removed but structure preserved
  }, []);
  
  // Track errors for observability
  useEffect(() => {
    if (error) {
      console.error('[PORTAL_ERROR]', {
        execution_id: executionId,
        error: error || 'Unknown error',
        timestamp: new Date().toISOString(),
        performance_metrics: perf.getMetrics()
      });
    }
  }, [error, executionId, perf]);

  // HISIP-013: Enhanced loading and error states
  if (loading || !execution || !execution.stages) {
    return (
      <div className="void-foundation min-h-screen flex items-center justify-center">
        <AtmosphericBackground />
        <div className="relative z-10 text-white text-center max-w-md">
          {/* Loading State */}
          {loading && !error && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="absolute inset-0 bg-cyan-500/20 rounded-full animate-ping" />
                <div className="absolute inset-0 bg-cyan-500/30 rounded-full animate-pulse" />
                <div className="relative w-full h-full flex items-center justify-center">
                  <Cpu className="w-8 h-8 text-cyan-400" />
                </div>
              </div>
              <p className="text-xl mb-2 text-white/90 font-mono">
                LOADING CREATIVE PORTAL
              </p>
              <p className="text-sm text-white/50 font-mono">
                Execution: {executionId?.slice(0, 8)}
              </p>
            </>
          )}
          
          {/* Error State */}
          {(error || notFound) && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="relative w-full h-full flex items-center justify-center">
                  <div className="text-red-400">
                    <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                </div>
              </div>
              
              {notFound ? (
                <>
                  <h2 className="text-2xl mb-2 text-white/90 font-mono">
                    EXECUTION NOT FOUND
                  </h2>
                  <p className="text-sm text-white/70 mb-2">
                    Execution ID: {executionId?.slice(0, 8)}
                  </p>
                  <div className="mt-4 px-4 py-3 bg-red-500/20 border border-red-500/40 rounded-lg">
                    <p className="text-red-300 text-sm mb-2">
                      The execution you're looking for doesn't exist or has been deleted.
                    </p>
                    <p className="text-red-300/70 text-xs">
                      This can happen if:
                    </p>
                    <ul className="text-red-300/70 text-xs mt-1 text-left list-disc list-inside">
                      <li>The execution was deleted</li>
                      <li>The URL contains a typo</li>
                      <li>You don't have permission to view it</li>
                    </ul>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="text-2xl mb-2 text-white/90 font-mono">
                    ERROR LOADING PORTAL
                  </h2>
                  <p className="text-sm text-white/70 mb-2">
                    Execution ID: {executionId?.slice(0, 8)}
                  </p>
                  <div className="mt-4 px-4 py-3 bg-red-500/20 border border-red-500/40 rounded-lg">
                    <p className="text-red-300 text-sm">{error}</p>
                  </div>
                </>
              )}
              
              <div className="mt-6 flex gap-3 justify-center">
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-cyan-500/20 border border-cyan-500/40 rounded-lg hover:bg-cyan-500/30 transition-all text-cyan-300 text-sm"
                >
                  Retry
                </button>
                <button
                  onClick={() => window.location.href = (window.location.pathname.startsWith('/dashboard') ? '/dashboard' : '') + '/executions'}
                  className="px-4 py-2 bg-purple-500/20 border border-purple-500/40 rounded-lg hover:bg-purple-500/30 transition-all text-purple-300 text-sm"
                >
                  View All Executions
                </button>
              </div>
            </>
          )}
          
          {/* No Data State (after loading, no error, but no execution) */}
          {!loading && !error && !execution && (
            <>
              <div className="w-16 h-16 mx-auto mb-4 relative">
                <div className="relative w-full h-full flex items-center justify-center">
                  <Layers className="w-12 h-12 text-gray-400" />
                </div>
              </div>
              <h2 className="text-2xl mb-2 text-white/90 font-mono">
                NO EXECUTION DATA
              </h2>
              <p className="text-sm text-white/50 mb-4">
                Unable to load execution details
              </p>
              <button
                onClick={() => window.location.href = (window.location.pathname.startsWith('/dashboard') ? '/dashboard' : '') + '/executions'}
                className="px-4 py-2 bg-cyan-500/20 border border-cyan-500/40 rounded-lg hover:bg-cyan-500/30 transition-all text-cyan-300 text-sm"
              >
                Back to Executions
              </button>
            </>
          )}
        </div>
      </div>
    );
  }
  
  // Calculate overall progress
  const completedStages = STAGES.filter(s => execution.stages[s]?.status === 'completed').length;
  const overallProgress = (completedStages / STAGES.length) * 100;
  
  // Get active stage
  const activeStage = STAGES.find(s => execution.stages[s]?.status === 'running');
  
  return (
    <div className="void-foundation min-h-screen text-white relative overflow-auto lg:overflow-hidden">
      <AtmosphericBackground />
      
      {/* Main Content */}
      <div className="relative z-10 p-4">
        {/* Header with metrics */}
        <div className="mb-6 space-y-4">
          <div className="flex items-start justify-between">
            {/* Title and Execution ID */}
            <div>
              <h1 className="text-3xl font-light text-gradient-cyan tracking-wider mb-1">
                CREATIVE PORTAL
              </h1>
              <p className="text-white/50 font-mono text-sm">
                EXECUTION: {executionId?.slice(0, 8).toUpperCase()}
              </p>
            </div>
            
            {/* Connection Status & Metrics */}
            <div className="flex gap-3 items-start">
              {/* Connection Status Indicator */}
              <div className="flex items-center gap-2">
                  <ConnectionDot
                    status={webSocketState.status}
                    isPollingFallback={webSocketState.isPollingFallback}
                  />
                  <ConnectionStatusIndicator
                    status={webSocketState.status}
                    isPollingFallback={webSocketState.isPollingFallback}
                    error={webSocketState.error}
                    lastUpdate={webSocketState.lastUpdate}
                    onReconnect={webSocketState.reconnect}
                    compact={true}
                    className="text-xs"
                  />
                </div>
              
              <div 
                className="rounded-lg border"
                style={{
                  padding: 'var(--space-5) var(--space-5)',
                  backgroundColor: 'var(--bg-overlay)',
                  borderColor: 'var(--accent-border-subtle)'
                }}
              >
                <div 
                  className="uppercase text-right"
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--fg-disabled)'
                  }}
                >
                  Progress
                </div>
                <div 
                  className="font-mono text-left"
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    color: 'var(--accent)'
                  }}
                >
                  {overallProgress.toFixed(0)}%
                </div>
              </div>
              <div 
                className="rounded-lg border"
                style={{
                  padding: 'var(--space-5) var(--space-5)',
                  backgroundColor: 'var(--bg-overlay)',
                  borderColor: 'var(--effect-purple)'
                }}
              >
                <div 
                  className="uppercase text-right"
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--fg-disabled)'
                  }}
                >
                  Quality
                </div>
                <div 
                  className="font-mono text-left"
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    color: avgQualityPercent >= 85 ? 'var(--success)' :
                           avgQualityPercent >= 70 ? 'var(--warning)' : 'var(--danger)'
                  }}
                >
                  {avgQualityPercent.toFixed(0)}%
                </div>
              </div>
              <div 
                className="rounded-lg border"
                style={{
                  padding: 'var(--space-5) var(--space-5)',
                  backgroundColor: 'var(--bg-overlay)',
                  borderColor: 'var(--success-border-subtle)'
                }}
              >
                <div 
                  className="uppercase text-right"
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--fg-disabled)'
                  }}
                >
                  Cost
                </div>
                <div 
                  className="font-mono text-left"
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    color: 'var(--success)'
                  }}
                >
                  ${(execution.cost_incurred || 0).toFixed(2)}
                </div>
              </div>
            </div>
          </div>

          {/* TEMP-H-002: Control Bar */}
          {executionId && (
            <ControlErrorBoundary>
              <ExecutionControlBar
                executionId={executionId}
                executionStatus={execution?.status || 'running'}
                className="mb-4"
              />
            </ControlErrorBoundary>
          )}

          {/* TEMP-H-002: Status Strip */}
          {executionId && (
            <ControlErrorBoundary>
              <ExecutionStatusStrip
                executionId={executionId}
                className="mb-4"
              />
            </ControlErrorBoundary>
          )}
        </div>
        
        {/* HISIP-013: Profile Summary */}
        {profileConfig && (
          <div className="mb-6">
            <ProfileSummary
              profile={profileConfig}
              projectId={executionData?.curriculum_id}
              compact={true}
              className="max-w-2xl mx-auto"
            />
          </div>
        )}
        
        {/* Classic Pipeline Visualization */}
        <div className="flex flex-col items-center justify-center -mt-4">
          <div className="relative">
          {/* Enhanced connection lines with gradients */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
            {/* Define gradients for energy flow */}
            <defs>
              <linearGradient id="flow-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor={colorSystem.processing} stopOpacity="0" />
                <stop offset="50%" stopColor={colorSystem.processing} stopOpacity="1" />
                <stop offset="100%" stopColor={colorSystem.processing} stopOpacity="0" />
              </linearGradient>
              <filter id="glow">
                <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                <feMerge>
                  <feMergeNode in="coloredBlur"/>
                  <feMergeNode in="SourceGraphic"/>
                </feMerge>
              </filter>
            </defs>
            
            {STAGES.map((stage, index) => {
              if (index === STAGES.length - 1) return null;
              // Create an arc that wraps around the brain with endpoints below
              const arcStart = 225; // Start angle in degrees (below left)
              // Calculate range properly for wrapping arc
              const totalAngle = 270; // Total arc span in degrees
              const angleStep = totalAngle / (STAGES.length - 1);
              const angle1 = (arcStart - (index * angleStep)) * Math.PI / 180;
              const angle2 = (arcStart - ((index + 1) * angleStep)) * Math.PI / 180;
              const radius = 180; // Reduced radius for more compact layout
              const x1 = 200 + Math.cos(angle1) * radius;
              const y1 = 200 + Math.sin(angle1) * radius;
              const x2 = 200 + Math.cos(angle2) * radius;
              const y2 = 200 + Math.sin(angle2) * radius;
              
              const currentStageComplete = execution.stages[stage]?.status === 'completed';
              const nextStageRunning = execution.stages[STAGES[index + 1]]?.status === 'running';
              const isActive = currentStageComplete && nextStageRunning;
              const isCompleted = currentStageComplete && execution.stages[STAGES[index + 1]]?.status === 'completed';
              
              return (
                <g key={`${stage}-connection`}>
                  <motion.line
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke={isActive ? colorSystem.processing : 
                           isCompleted ? colorSystem.excellence : 
                           'color-mix(in srgb, var(--fg-primary) 30%, transparent)'}
                    strokeWidth={isActive ? "4" : isCompleted ? "3" : "2"}
                    strokeDasharray={isActive ? "5,5" : undefined}
                    filter={isActive || isCompleted ? "url(#glow)" : undefined}
                    initial={{ pathLength: 0, opacity: 0 }}
                    animate={{ 
                      pathLength: 1, 
                      opacity: isActive ? 1 : isCompleted ? 0.7 : 0.4
                    }}
                    transition={{ duration: 1, delay: index * 0.1 }} // Note: Framer Motion requires numeric values
                  >
                    {isActive && (
                      <animate
                        attributeName="stroke-dashoffset"
                        values="10;0"
                        dur="1s"
                        repeatCount="indefinite"
                      />
                    )}
                  </motion.line>
                  
                  {/* Energy pulse animation for active connections */}
                  {isActive && (
                    <>
                      <motion.circle
                        r="6"
                        fill={colorSystem.processing}
                        filter="url(#glow)"
                        initial={{ x: x1, y: y1 }}
                        animate={{ x: x2, y: y2 }}
                        transition={{
                          duration: 2, // Note: Framer Motion requires numeric values
                          repeat: Infinity,
                          ease: "linear"
                        }}
                      />
                      {/* Additional glow effect for active connections */}
                      <line
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={colorSystem.processing}
                        strokeWidth="8"
                        opacity="0.3"
                        filter="url(#glow)"
                      />
                    </>
                  )}
                </g>
              );
            })}
          </svg>
          
          {/* Stage nodes */}
          <div className="relative" style={{ width: '400px', height: '400px' }}>
            {STAGES.map((stage, index) => {
              // Match the arc layout from the connections
              const arcStart = 225; // Start angle in degrees (below left)
              // Calculate range properly for wrapping arc
              const totalAngle = 270; // Total arc span in degrees
              const angleStep = totalAngle / (STAGES.length - 1);
              const angle = (arcStart - (index * angleStep)) * Math.PI / 180;
              const radius = 180; // Same radius as connections
              const x = 200 + Math.cos(angle) * radius;
              const y = 200 + Math.sin(angle) * radius;
              
              return (
                <div
                  key={stage}
                  className="absolute"
                  style={{
                    left: `${x}px`,
                    top: `${y}px`,
                    transform: 'translate(-50%, -50%)'
                  }}
                >
                  <StageNode
                    stage={stage}
                    execution={execution}
                    isActive={stage === activeStage}
                    isSelected={stage === selectedStage}
                    onClick={() => setSelectedStage(stage)}
                    onFeedback={() => {
                      setActiveFeedbackStage(stage);
                      setShowFeedbackPanel(true);
                    }}
                    onIterations={() => {
                      setSelectedStage(stage);
                      setShowIterationHistory(true);
                    }}
                    qualityPercent={(() => {
                      const qm = qualityMetricsByStage[stage];
                      return typeof qm?.overall === 'number' ? qm.overall * 100 : 0;
                    })()}
                    iterationsCount={(iterationsByStage[stage] || []).length}
                    showEnhancedIndicators={true}
                    rubricByStage={rubricByStage}
                  />
                </div>
              );
            })}
            
            {/* Enhanced center consciousness */}
            <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.5 }} // Note: Framer Motion requires numeric values
                className="relative"
              >
                {/* Consciousness glow */}
                <motion.div 
                  className="absolute inset-0 rounded-full blur-3xl bg-purple-500/30"
                  animate={{ 
                    scale: [1, 1.2, 1],
                    opacity: [0.3, 0.5, 0.3]
                  }}
                  transition={{
                    duration: 3, // Note: Framer Motion requires numeric values, syncs with --motion-duration-slow
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                  style={{
                    width: '120px',
                    height: '120px',
                    marginLeft: '-60px',
                    marginTop: '-60px',
                    left: '50%',
                    top: '50%'
                  }}
                />
                
                {/* Brain icon with pulsing glow */}
                <motion.div
                  animate={{ 
                    scale: [1, 1.1, 1],
                    filter: [
                      'drop-shadow(0 0 20px color-mix(in srgb, var(--effect-purple) 40%, transparent))',
                      'drop-shadow(0 0 40px color-mix(in srgb, var(--effect-purple) 80%, transparent))',
                      'drop-shadow(0 0 20px color-mix(in srgb, var(--effect-purple) 40%, transparent))'
                    ]
                  }}
                  transition={{ 
                    duration: 3, // Note: Framer Motion requires numeric values, syncs with --motion-duration-slow
                    repeat: Infinity, 
                    ease: "easeInOut" 
                  }}
                  className="relative z-10"
                >
                  <Brain className="w-16 h-16 text-purple-400" />
                </motion.div>
                
                {/* Active persona indicator - Centered */}
                {activeStage && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 text-center w-full"
                  >
                    <div className="text-sm text-purple-300 whitespace-nowrap flex justify-center">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" />
                        Processing {STAGE_CONFIG[activeStage].name}
                      </span>
                    </div>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </div>
        </div>
        
        {/* Stage Details Panel - Positioned Under Pipeline */}
        {selectedStage && (
          <div className="lg:fixed lg:inset-0 lg:z-40 lg:pointer-events-none">
            <div 
              className="hidden lg:block absolute top-0 bottom-0 left-20 right-0 pointer-events-auto"
              onClick={() => setSelectedStage(null)}
            />
            {/* On small screens, render in-flow; on large, anchor to bottom */}
            <div className="lg:absolute lg:left-0 lg:right-0 lg:bottom-0 flex justify-center items-start p-4 lg:items-end lg:pl-20">
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 50 }}
                className="w-full max-w-3xl lg:w-[80%] pointer-events-auto relative"
              onClick={(e) => e.stopPropagation()}
              >
                {/* Output Display Column - Slide from left when stage has output (large screens) */}
                {execution.stages[selectedStage]?.output && (
                  <motion.div
                    initial={{ x: -300, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: -300, opacity: 0 }}
                    className="hidden lg:block fixed left-24 top-1/2 transform -translate-y-1/2 w-80 z-50 pointer-events-auto"
                  >
                    <HolographicCard glow="success" intensity="medium" className="bg-black/90 backdrop-blur-xl border-emerald-500/20 max-h-[80vh] overflow-hidden">
                      <div className="p-4 border-b border-white/10">
                        <h3 className="text-lg font-mono text-emerald-400 uppercase tracking-wider">
                          {STAGE_CONFIG[selectedStage].name} OUTPUT
                        </h3>
                      </div>
                      <div className="p-4 max-h-[60vh] overflow-y-auto">
                        <div className="space-y-4">
                          {/* HISIP-013: Enhanced media preview with manifest support */}
                          {/* Direct output rendering for large screens */}
                          {execution.stages[selectedStage]?.output ? (
                            <div className="space-y-3">
                              <div className="flex items-center justify-between">
                                <h3 className="text-sm font-mono text-emerald-400 uppercase tracking-wider">Stage Output</h3>
                                {execution.stages.script?.audioUrl && (
                                  <AudioPlayer 
                                    audioUrl={execution.stages.script.audioUrl}
                                    className="ml-2"
                                  />
                                )}
                              </div>
                              <div className="text-sm text-white/70">
                                {execution.stages[selectedStage]?.output?.type === 'text' && (
                                  <div className="font-mono whitespace-pre-wrap max-h-[45vh] overflow-y-auto custom-scrollbar bg-black/20 rounded p-3">
                                    {execution.stages[selectedStage]?.output?.content || 'No content available'}
                                  </div>
                                )}
                                {execution.stages[selectedStage]?.output?.type === 'image' && (
                                  <div className="bg-black/20 rounded p-3">
                                    <img 
                                      src={execution.stages[selectedStage]?.output?.url}
                                      alt={`${selectedStage} output`}
                                      className="w-full max-h-[40vh] object-contain rounded"
                                    />
                                  </div>
                                )}
                                {execution.stages[selectedStage]?.output?.type === 'video' && (
                                  <div className="bg-black/20 rounded p-3">
                                    <video 
                                      src={execution.stages[selectedStage]?.output?.url}
                                      controls
                                      className="w-full max-h-[40vh] rounded"
                                      preload="metadata"
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center h-32 text-white/40">
                              <p className="text-sm">No {selectedStage} output available yet</p>
                            </div>
                          )}
                          
                          {/* Quality Score and Rubric Status */}
                          <div className="flex items-center justify-between pt-3 border-t border-white/10">
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/60">Quality:</span>
                              <span className="text-sm font-mono text-emerald-400">
                                {Math.round(execution.stages[selectedStage]?.quality || 0)}%
                              </span>
                            </div>
                            
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white/60">Rubric:</span>
                              <RubricBadge
                                rubric={rubricByStage[selectedStage]}
                                size="sm"
                                showScore={true}
                                showTooltip={true}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </HolographicCard>
                  </motion.div>
                )}
 
               <div>
             <HolographicCard glow="cyan" intensity="medium" className="bg-black/90 backdrop-blur-xl border-cyan-500/20 lg:max-h-[60vh] lg:overflow-hidden">
               {/* Custom Header with Iterations and Status */}
               <div className="p-6 pb-4 border-b border-white/10">
                 <div className="flex items-start justify-between">
                   <div className="flex items-center gap-3">
                     {React.createElement(STAGE_CONFIG[selectedStage].icon, { className: "w-5 h-5 text-cyan-400" })}
                     <div>
                       <h3 className="text-lg font-mono text-cyan-400 uppercase tracking-wider">
                         {STAGE_CONFIG[selectedStage].name.toUpperCase()} STAGE
                       </h3>
                       <p className="text-sm text-white/60 mt-1">
                         {execution.stages[selectedStage]?.persona || 'Processing'}
                       </p>
                     </div>
                   </div>
                   
                   {/* Header Actions: Iterations, Status, and Close */}
                   <div className="flex items-center gap-4 text-sm">
                     {/* Iteration Count */}
                     <div className="flex items-center gap-2">
                       <span className="text-white/60">Iterations:</span>
                       <span className="font-mono text-cyan-400">
                         {(() => {
                           const list = (iterationsByStage[selectedStage] || []);
                           const currentIterations = list.reduce((max: number, it: any) => {
                             const n = Number(it?.iteration_number) || 0;
                             return Math.max(max, n);
                           }, 0);
                          const ctx = (executionData?.execution?.execution_context) || (rawExecution as any)?.execution_context || {};
                          // Use real-time max_iterations from store metadata first, then context, then iteration count, then fallback to 5
                          const maxIterations = storeMaxIterations[selectedStage] || 
                                               ctx?.[selectedStage]?.max_iterations || 
                                               ctx?.max_iterations || 
                                               ((iterationsByStage[selectedStage] || []).length || 5);
                          return `${currentIterations}/${maxIterations}`;
                         })()}
                       </span>
                     </div>
                     
                     {/* Status */}
                     <div className="flex items-center gap-2">
                       <span className="text-white/60">Status:</span>
                       <span className={cn(
                         "font-mono uppercase text-xs px-2 py-1 rounded",
                         execution.stages[selectedStage]?.status === 'completed' ? 'text-emerald-400 bg-emerald-400/10' :
                         execution.stages[selectedStage]?.status === 'running' ? 'text-cyan-400 bg-cyan-400/10 animate-pulse' :
                         execution.stages[selectedStage]?.status === 'failed' ? 'text-red-400 bg-red-400/10' : 'text-gray-400 bg-gray-400/10'
                       )}>
                         {execution.stages[selectedStage]?.status}
                       </span>
                     </div>
                   </div>
                 </div>
               </div>
               
               <CardContent className="py-1 pb-6">
                {/* Small-Screen Output Panel (slides down below header) */}
                {execution.stages[selectedStage]?.output && (
                  <motion.div
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    exit={{ y: -50, opacity: 0 }}
                    className="block lg:hidden p-4 mb-4 bg-black/30 rounded-lg border border-emerald-500/20"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-sm font-mono text-emerald-400 uppercase tracking-wider">Stage Output</h3>
                      {execution.stages.script?.audioUrl && (
                        <AudioPlayer 
                          audioUrl={execution.stages.script.audioUrl}
                          className="ml-2"
                        />
                      )}
                    </div>
                    <div className="text-sm text-white/70">
                      {execution.stages[selectedStage]?.output?.type === 'text' && (
                        <div className="font-mono whitespace-pre-wrap max-h-60 overflow-y-auto custom-scrollbar bg-black/20 rounded p-3">
                          {execution.stages[selectedStage]?.output?.content || 'No content available'}
                        </div>
                      )}
                      {execution.stages[selectedStage]?.output?.type === 'image' && (
                        <div className="bg-black/20 rounded p-3">
                          <img 
                            src={execution.stages[selectedStage]?.output?.url}
                            alt={`${selectedStage} output`}
                            className="w-full max-h-48 object-contain rounded"
                          />
                        </div>
                      )}
                      {execution.stages[selectedStage]?.output?.type === 'video' && (
                        <div className="bg-black/20 rounded p-3">
                          <video 
                            src={execution.stages[selectedStage]?.output?.url}
                            controls
                            className="w-full max-h-48 rounded"
                            preload="metadata"
                          />
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}

                {/* Two-column layout: Stage Info + Output Preview */}
                <div className={cn(
                  "grid gap-6",
                  "grid-cols-1"
                )}>
                  
                  {/* Left Column: Stage Information */}
                  <div className="space-y-4">
                    
                    {/* Interactive Quality Threshold Slider */}
                    <div className="mb-6">
                      <InteractiveThresholdSlider
                        currentQuality={qualityMetricsByStage[selectedStage]?.overall || 0}
                        targetThreshold={stageThresholds?.passMin ? stageThresholds.passMin / 100 : 0.8}
                        fallbackThreshold={stageThresholds?.fallbackMin ? stageThresholds.fallbackMin / 100 : 0.7}
                        onThresholdChange={(_target, _fallback) => updateStageThresholds()}
                        className="text-white"
                        disabled={false}
                      />
                    </div>
                    
                    
                    
                    {/* Persona Badge */}
                    {execution.stages[selectedStage]?.persona && (
                      <div className="mb-4">
                        <PersonaBadge 
                          persona={{
                            name: execution.stages[selectedStage]?.persona || 'The Operator',
                            model: 'Claude Opus 4.1',
                            action: execution.stages[selectedStage]?.status === 'running' ? 'Processing' : 'Ready',
                            role: 'generator'
                          }}
                          isActive={execution.stages[selectedStage]?.status === 'running'}
                        />
                      </div>
                    )}

                    {/* TEMP-H-001: New QC Panel with persona, rubric, and scores */}
                    <div className="mb-6">
                      <QCPanel 
                        executionId={execution.id} 
                        stage={selectedStage}
                      />
                    </div>

                    {/* TEMP-H-002: Stage Actions for human-in-the-loop controls */}
                    {executionId && (
                      <ControlErrorBoundary>
                        <div className="mb-6">
                          <div className="mb-3">
                            <h4 className="text-sm font-medium text-slate-300 uppercase tracking-wider">
                              Stage Controls
                            </h4>
                          </div>
                          <StageActions
                            executionId={executionId}
                            stage={selectedStage}
                            currentStage={controlQueries?.currentStage || null}
                          />
                        </div>
                      </ControlErrorBoundary>
                    )}

                    {/* TEMP-H-001: New Iterations List with accepted markers */}
                    <div className="mb-6">
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-slate-300 uppercase tracking-wider">
                          Stage Iterations
                        </h4>
                      </div>
                      <IterationsList 
                        executionId={execution.id} 
                        stage={selectedStage}
                      />
                    </div>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => setShowIterationHistory(true)}
                        className="flex-1 btn-future text-cyan-400 border-cyan-400/50 hover:bg-cyan-400/10 py-1.5 text-xs"
                      >
                        <Layers className="w-3 h-3 inline-block mr-1" />
                        <span className="hidden sm:inline">ITERATIONS</span>
                        <span className="sm:hidden">ITER</span>
                      </button>
                      <button
                        onClick={() => setShowTimeline(true)}
                        className="flex-1 btn-future text-orange-400 border-orange-400/50 hover:bg-orange-400/10 py-1.5 text-xs"
                      >
                        <Clock className="w-3 h-3 inline-block mr-1" />
                        <span className="hidden sm:inline">TIMELINE</span>
                        <span className="sm:hidden">TIME</span>
                      </button>
                      <button
                        onClick={() => {
                          setActiveFeedbackStage(selectedStage);
                          setShowFeedbackPanel(true);
                        }}
                        className="flex-1 btn-future text-purple-400 border-purple-400/50 hover:bg-purple-400/10 py-1.5 text-xs"
                      >
                        <MessageSquare className="w-3 h-3 inline-block mr-1" />
                        <span className="hidden sm:inline">FEEDBACK</span>
                        <span className="sm:hidden">FDBK</span>
                      </button>
                      <button
                        onClick={() => {
                          setActiveFeedbackStage(selectedStage);
                          setShowCascadePlanner(true);
                        }}
                        className="flex-1 btn-future text-emerald-400 border-emerald-400/50 hover:bg-emerald-400/10 py-1.5 text-xs"
                      >
                        <Brain className="w-3 h-3 inline-block mr-1" />
                        <span className="hidden sm:inline">CASCADE</span>
                        <span className="sm:hidden">CSCD</span>
                      </button>
                      <button
                        onClick={() => navigate(`/executions/${executionId}/settings`)}
                        className="flex-1 btn-future text-blue-400 border-blue-400/50 hover:bg-blue-400/10 py-1.5 text-xs"
                      >
                        <Settings className="w-3 h-3 inline-block mr-1" />
                        <span className="hidden sm:inline">SETTINGS</span>
                        <span className="sm:hidden">SET</span>
                      </button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </HolographicCard>
              </div>
            </motion.div>
            </div>
          </div>
        )}
      </div>
      
      {/* Modals - HIGH SIGNAL versions */}
      <AnimatePresence>
        {showFeedbackPanel && activeFeedbackStage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-8"
            onClick={() => { setShowFeedbackPanel(false); setActiveFeedbackStage(null); }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
            <div className="bg-black/95 backdrop-blur-xl rounded-2xl border border-cyan-500/20 shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 p-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-cyan-400" />
                    <h3 className="text-lg font-light text-white/90 tracking-wider uppercase">
                      {STAGE_CONFIG[activeFeedbackStage].name} Feedback
                    </h3>
                  </div>
                  <button
                    onClick={() => { setShowFeedbackPanel(false); setActiveFeedbackStage(null); }}
                    className="p-2 rounded-lg hover:bg-white/10 transition-all"
                  >
                    <span className="text-white/50 text-lg">×</span>
                  </button>
                </div>
              </div>
              <FeedbackPanel
                execution={rawExecution || ({} as ExecutionStatus)}
                stage={activeFeedbackStage}
                currentIteration={{
                  iteration_number: 1,
                  what_worked: [],
                  what_failed: [],
                  timestamp: new Date().toISOString(),
                  scores: {
                    hook_effectiveness: 0,
                    narrative_clarity: 0,
                    visual_coherence: 0,
                    persona_consistency: 0,
                    anti_cliche_score: 0,
                    overall: execution.stages[activeFeedbackStage]?.quality || 0
                  },
                  decision: 'pending' as const,
                  ai_feedback: '',
                  persona_used: STAGE_CONFIG[activeFeedbackStage].persona,
                  is_best_attempt: false
                } as any}
                onFeedbackSubmit={(feedback: any) => {
                  console.log('Feedback submitted:', feedback);
                  setShowFeedbackPanel(false);
                  setShowCascadePlanner(true);
                }}
                onClose={() => {
                  setShowFeedbackPanel(false);
                  setActiveFeedbackStage(null);
                }}
                className="max-w-2xl w-full"
              />
            </div>
            </motion.div>
          </motion.div>
        )}
        
        {showCascadePlanner && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-8"
            onClick={() => setShowCascadePlanner(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
            <div className="bg-black/95 backdrop-blur-xl rounded-2xl border border-purple-500/20 shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-purple-500/10 to-emerald-500/10 p-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <GitBranch className="w-5 h-5 text-purple-400" />
                    <h3 className="text-lg font-light text-white/90 tracking-wider uppercase">
                      Cascade Planner
                    </h3>
                  </div>
                  <button
                    onClick={() => setShowCascadePlanner(false)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-all"
                  >
                    <span className="text-white/50 text-lg">×</span>
                  </button>
                </div>
              </div>
              <CascadePlanner
                execution={rawExecution || ({} as ExecutionStatus)}
                feedback={{
                  type: 'user_feedback',
                  decision: 'improve',
                  feedback: '',
                  priority: 'supplement',
                  preserve_elements: [],
                  specific_issues: [],
                  suggestions: [],
                  timestamp: new Date().toISOString(),
                  stage: activeFeedbackStage,
                  iteration_number: 1
                } as any}
                onExecuteCascade={(plan: any, scope: any, targetStage?: any) => {
                  console.log('Cascade plan confirmed:', plan, scope, targetStage);
                  setActiveCascadeId('cascade-123'); // Would get real ID from API
                  setShowCascadePlanner(false);
                }}
                onCancel={() => setShowCascadePlanner(false)}
                className="max-w-4xl w-full"
              />
            </div>
            </motion.div>
          </motion.div>
        )}
        
        {activeCascadeId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-8"
            onClick={() => setActiveCascadeId(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
            <div className="bg-black/95 backdrop-blur-xl rounded-2xl border border-emerald-500/20 shadow-2xl overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-500/10 to-cyan-500/10 p-4 border-b border-white/10">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Activity className="w-5 h-5 text-emerald-400 animate-pulse" />
                    <h3 className="text-lg font-light text-white/90 tracking-wider uppercase">
                      Cascade Progress
                    </h3>
                  </div>
                  <button
                    onClick={() => setActiveCascadeId(null)}
                    className="p-2 rounded-lg hover:bg-white/10 transition-all"
                  >
                    <span className="text-white/50 text-lg">×</span>
                  </button>
                </div>
              </div>
              <CascadeProgressMonitor
                cascadeId={activeCascadeId}
                onCancel={() => setActiveCascadeId(null)}
                className="max-w-3xl w-full"
              />
            </div>
            </motion.div>
          </motion.div>
        )}
        
        {showIterationHistory && selectedStage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-8"
            onClick={() => setShowIterationHistory(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative"
            >
              <IterationHistory
                stage={selectedStage}
                executionId={executionId}
                iterations={(iterationsByStage[selectedStage] || []).map((it: any) => ({
                  iteration_number: it.iteration_number,
                  trigger: it.trigger_type === 'user_feedback' ? 'user_feedback' : 'ai_evaluation',
                  decision: it.decision,
                  feedback_source: it.trigger_type === 'user_feedback' ? { type: 'user', user_input: it.feedback } : { type: 'gpt5', ai_evaluation: { feedback: it.feedback, decision: it.decision, confidence: 1 } },
                  scores: { 
                    overall: (() => { const s = it.scores || {}; const v = s.overall ?? s.composite_score ?? 0; return v > 1 ? v : Math.round(v * 100); })(),
                    hook_effectiveness: it.scores?.hook_effectiveness ?? 0,
                    narrative_clarity: it.scores?.narrative_clarity ?? 0,
                    visual_coherence: it.scores?.visual_coherence ?? 0,
                    persona_consistency: it.scores?.persona_consistency ?? 0,
                    anti_cliche_score: it.scores?.anti_cliche_score ?? 0
                  },
                  improvements: it.improved_elements || [],
                  preserved_from_previous: it.preserved_elements || [],
                  persona_used: it.persona_name || 'AI',
                  timestamp: it.created_at,
                  is_best_attempt: it.is_best_attempt,
                  stage: selectedStage,
                  assets: it.assets || [],
                  processing_time_seconds: undefined,
                  cost_usd: undefined
                }))}
                onIterationSelect={(iteration: number) => console.log('Selected iteration:', iteration)}
                onClose={() => setShowIterationHistory(false)}
                className="max-w-3xl w-full bg-gray-900/95 backdrop-blur-xl rounded-2xl p-8 border border-white/10 shadow-2xl"
              />
            </motion.div>
          </motion.div>
        )}
        
        {/* TEMP-H-003: Timeline Modal */}
        {showTimeline && executionId && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4"
            onClick={() => setShowTimeline(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-6xl max-h-[90vh]"
            >
              <div className="bg-black/95 backdrop-blur-xl rounded-2xl border border-orange-500/20 shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-orange-500/10 to-purple-500/10 p-4 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-orange-400" />
                      <h3 className="text-lg font-light text-white/90 tracking-wider uppercase">
                        Events Timeline
                      </h3>
                    </div>
                    <button
                      onClick={() => setShowTimeline(false)}
                      className="p-2 rounded-lg hover:bg-white/10 transition-all"
                    >
                      <span className="text-white/50 text-lg">×</span>
                    </button>
                  </div>
                </div>
                <div className="p-6">
                  <ControlErrorBoundary>
                    <ExecutionTimeline
                      executionId={executionId}
                      height={600}
                      compactMode={false}
                      showFilters={true}
                      className="w-full"
                    />
                  </ControlErrorBoundary>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
        
        {/* FIX-004: Enhanced Iteration Details Modal */}
        {showIterationDetailsModal && selectedStage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-8"
            onClick={() => setShowIterationDetailsModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative"
            >
              <div className="bg-black/95 backdrop-blur-xl rounded-2xl border border-cyan-500/20 shadow-2xl overflow-hidden">
                <div className="bg-gradient-to-r from-cyan-500/10 to-purple-500/10 p-4 border-b border-white/10">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Layers className="w-5 h-5 text-cyan-400" />
                      <h3 className="text-lg font-light text-white/90 tracking-wider uppercase">
                        {STAGE_CONFIG[selectedStage].name} - Enhanced Iterations (FIX-004)
                      </h3>
                    </div>
                    <button
                      onClick={() => setShowIterationDetailsModal(false)}
                      className="p-2 rounded-lg hover:bg-white/10 transition-all"
                    >
                      <span className="text-white/50 text-lg">×</span>
                    </button>
                  </div>
                </div>
                
                <div className="p-6 max-w-6xl max-h-[80vh] overflow-auto">
                  {/* Iteration Timeline Component */}
                  <div className="mb-6">
                    <h4 className="text-sm font-mono text-cyan-400 uppercase tracking-wider mb-4">Iteration Timeline</h4>
                    <IterationTimeline
                      iterations={(iterationsByStage[selectedStage] || []).map((it: any) => ({
                        id: `${selectedStage}-${it.iteration_number}`,
                        iteration_number: it.iteration_number,
                        content: it.content || {},
                        scores: {
                          actual: Math.min(1, Math.max(0, (it.scores?.overall ?? it.quality_score ?? 0))),
                          threshold: 0.7,
                          decision: it.decision === 'accept' ? 'accept' : it.decision === 'improve' ? 'improve' : 'reject'
                        },
                        cost: it.cost_usd || 0,
                        generated_at: new Date(it.created_at),
                        is_selected: it.is_selected || false
                      }))}
                      maxIterations={((rawExecution as any)?.execution_context?.[selectedStage]?.max_iterations || 5)}
                      currentIteration={Math.max(1, Math.min((iterationsByStage[selectedStage] || []).length || 1, (iterationsByStage[selectedStage] || []).reduce((max: number, it: any) => Math.max(max, it.iteration_number || 0), 0)))}
                      onIterationClick={(iteration: any) => {
                        console.log('Timeline iteration selected:', iteration.id);
                      }}
                    />
                  </div>
                  
                  {/* Iteration Previews Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(iterationsByStage[selectedStage] || []).map((it: any, index: number) => (
                      <IterationPreview
                        key={index}
                        iteration={{
                          id: `${selectedStage}-${it.iteration_number}`,
                          iteration_number: it.iteration_number,
                          content: it.content || { text: `Iteration ${it.iteration_number} content` },
                          scores: {
                            actual: Math.min(1, Math.max(0, (it.scores?.overall ?? it.quality_score ?? 0))),
                            threshold: 0.7,
                            decision: it.decision === 'accept' ? 'accept' : it.decision === 'improve' ? 'improve' : 'reject'
                          },
                          cost: it.cost_usd || 0,
                          generated_at: new Date(it.created_at),
                          is_selected: it.is_selected || false
                        }}
                        stage={selectedStage}
                        isSelected={!!it.is_selected}
                        onSelect={() => {
                          console.log('Preview iteration selected:', `${selectedStage}-${it.iteration_number}`);
                        }}
                        onView={() => {
                          console.log('Preview iteration viewed:', `${selectedStage}-${it.iteration_number}`);
                        }}
                      />
                    ))}
                  </div>
                  
                  {(iterationsByStage[selectedStage] || []).length === 0 && (
                    <div className="text-center py-12">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-500/10 flex items-center justify-center">
                        <Layers className="w-8 h-8 text-gray-400" />
                      </div>
                      <p className="text-gray-400 font-mono">No iterations available for {STAGE_CONFIG[selectedStage].name}</p>
                      <p className="text-gray-500 text-sm mt-2">Iterations will appear here as they are generated</p>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
}